import React, { useState, useEffect, useRef } from 'react';
import { FaSync, FaPlus, FaEdit, FaTrash, FaSearch, FaToggleOn, FaToggleOff, FaPercentage } from 'react-icons/fa';
import { FaCreditCard } from 'react-icons/fa6';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';

const KASALAR = ['FAYNPAY','NEOPAYS','ULTRAPAY','FULGUR','ANINDA','MİNİ','ENHİZLİ','DIS_KASA', 'XPAY'];
const KASA_RENK = { FAYNPAY:'#f27127',NEOPAYS:'#3b82f6',ULTRAPAY:'#a855f7',FULGUR:'#22c55e',ANINDA:'#ef4444','MİNİ':'#f59e0b',ENHİZLİ:'#14b8a6',DIS_KASA:'#64748b', XPAY:'#d3c90b' };
const BOSH_FORM = { _id:'',kasa:'FAYNPAY',displayName:'',aktif:true,komisyon:{yatirim:0,cekim:0,tahsilat:0} };

/* ── MODAL ─────────────────────────────────────────────────────────────── */
const Modal = ({ open, onClose, title, subtitle, width=520, children }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div style={MS.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...MS.panel, maxWidth: width }}>
        <div style={MS.topBar}>
          <div>
            <p style={MS.modalTitle}>{title}</p>
            {subtitle && <p style={MS.modalSub}>{subtitle}</p>}
          </div>
          <button style={MS.xBtn} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div style={MS.body}>{children}</div>
      </div>
    </div>
  );
};

const MS = {
  overlay:{ position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:20 },
  panel:{ width:'100%',background:'#111316',border:'1px solid rgba(255,255,255,0.07)',borderRadius:18,boxShadow:'0 32px 80px rgba(0,0,0,0.6)' },
  topBar:{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',padding:'18px 24px 16px',borderBottom:'1px solid rgba(255,255,255,0.05)' },
  modalTitle:{ color:'#f1f5f9',fontSize:'1rem',fontWeight:700,margin:0,letterSpacing:'-0.01em' },
  modalSub:{ color:'#64748b',fontSize:'0.78rem',margin:'4px 0 0' },
  xBtn:{ background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:8,width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#64748b',flexShrink:0 },
  body:{ padding:'20px 24px' },
};

/* ── INPUT HELPERS ─────────────────────────────────────────────────────── */
const Field = ({ label, children, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
    <label style={{ display:'block',color:'#475569',fontSize:'0.7rem',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:7 }}>{label}</label>
    {children}
  </div>
);

const textInputStyle = (disabled) => ({
  width:'100%',padding:'10px 13px',background:disabled?'rgba(255,255,255,0.02)':'#0c0e10',
  border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,color:disabled?'#475569':'#f1f5f9',
  fontSize:'0.88rem',boxSizing:'border-box',outline:'none',cursor:disabled?'not-allowed':'text',
});

/* ── KOMİSYON INPUT ─────────────────────────────────────────────────────── */
const KomInput = ({ label, color, value, onChange }) => (
  <div style={{ background:'rgba(255,255,255,0.025)',borderRadius:10,padding:'10px 11px',border:`1px solid ${color}18` }}>
    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7 }}>
      <span style={{ color:'#475569',fontSize:'0.63rem',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em' }}>{label}</span>
      <span style={{ color,fontSize:'0.68rem',fontWeight:700,background:`${color}15`,padding:'1px 6px',borderRadius:4 }}>%{value||0}</span>
    </div>
    <div style={{ display:'flex',alignItems:'center',gap:5 }}>
      <input type="number" min="0" max="100" step="0.1" value={value}
        onChange={e => onChange(parseFloat(e.target.value)||0)}
        style={{ flex:1,minWidth:0,padding:'7px 6px',background:'#0c0e10',border:`1px solid ${color}28`,borderRadius:7,color,fontSize:'0.9rem',fontWeight:700,textAlign:'center',outline:'none' }}/>
      <span style={{ color:'#334155',fontSize:'0.75rem',fontWeight:600,flexShrink:0 }}>%</span>
    </div>
  </div>
);

/* ── ÖNIZLEME KARTI ─────────────────────────────────────────────────────── */
const PreviewCard = ({ label, oran, tutar, renk }) => {
  const k = parseFloat(((tutar*(oran??0))/100).toFixed(2));
  const fmt = n => new Intl.NumberFormat('tr-TR',{minimumFractionDigits:2}).format(n);
  return (
    <div style={{ background:'rgba(255,255,255,0.015)',border:`1px solid ${renk}15`,borderRadius:9,padding:'10px 12px' }}>
      <p style={{ color:'#334155',fontSize:'0.62rem',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',margin:'0 0 6px' }}>{label}</p>
      <p style={{ color:renk,fontWeight:800,fontSize:'0.88rem',margin:'0 0 2px',fontVariantNumeric:'tabular-nums' }}>{fmt(k)} ₺</p>
      <p style={{ color:'#475569',fontSize:'0.7rem',margin:0 }}>Net {fmt(tutar-k)} ₺</p>
    </div>
  );
};

/* ── ICON BUTTON ─────────────────────────────────────────────────────────── */
const IconBtn = ({ children, color, onClick, title }) => (
  <button className="oy-icon-btn" title={title} onClick={onClick}
    style={{ width:26,height:26,borderRadius:7,border:'1px solid rgba(255,255,255,0.05)',background:'rgba(255,255,255,0.03)',color:'#475569',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',transition:'all 0.15s',flexShrink:0 }}
    onMouseEnter={e=>{ e.currentTarget.style.color=color; e.currentTarget.style.borderColor=color+'40'; e.currentTarget.style.background=color+'12'; }}
    onMouseLeave={e=>{ e.currentTarget.style.color='#475569'; e.currentTarget.style.borderColor='rgba(255,255,255,0.05)'; e.currentTarget.style.background='rgba(255,255,255,0.03)'; }}
  >{children}</button>
);

/* ── ANA SAYFA ─────────────────────────────────────────────────────────── */
const OdemeYöntemiYonetimi = () => {
  const [yöntemler,setYontemler]   = useState([]);
  const [loading,setLoading]       = useState(true);
  const [search,setSearch]         = useState('');
  const [kasaFiltre,setKasaFiltre] = useState('');
  const [modal,setModal]           = useState(null); // null | 'form' | 'komisyon'
  const [secili,setSecili]         = useState(null);
  const [form,setForm]             = useState(BOSH_FORM);
  const firstRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = kasaFiltre ? `?kasa=${kasaFiltre}` : '';
      const res = await axiosInstance.get(`/odeme-yontemi/all${q}`);
      setYontemler(res.data);
    } catch { toast.error('Veriler yüklenemedi.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [kasaFiltre]);
  useEffect(() => { if (modal) setTimeout(()=>firstRef.current?.focus(),80); }, [modal]);

  const closeModal   = () => setModal(null);
  const openEkle     = () => { setForm(BOSH_FORM); setSecili(null); setModal('form'); };
  const openDüzenle  = y => { setSecili(y); setForm({ _id:y._id,kasa:y.kasa,displayName:y.displayName,aktif:y.aktif,komisyon:{yatirim:y.komisyon?.yatirim??0,cekim:y.komisyon?.cekim??0,tahsilat:y.komisyon?.tahsilat??0} }); setModal('form'); };
  const openKomisyon = y => { setSecili(y); setForm(p=>({...p,komisyon:{yatirim:y.komisyon?.yatirim??0,cekim:y.komisyon?.cekim??0,tahsilat:y.komisyon?.tahsilat??0}})); setModal('komisyon'); };

  const handleKaydet = async () => {
    try {
      if (!secili)                 { await axiosInstance.post('/odeme-yontemi/add',form); toast.success('Yöntem eklendi.'); }
      else if (modal==='komisyon') { await axiosInstance.patch(`/odeme-yontemi/update/${secili._id}`,{komisyon:form.komisyon}); toast.success('Komisyon güncellendi.'); }
      else                         { await axiosInstance.patch(`/odeme-yontemi/update/${secili._id}`,form); toast.success('Güncellendi.'); }
      closeModal(); fetchData();
    } catch { toast.error('İşlem başarısız.'); }
  };

  const handleSil = async (y) => {
    if (!window.confirm(`"${y.displayName}" silinsin mi?`)) return;
    try { await axiosInstance.delete(`/odeme-yontemi/delete/${y._id}`); toast.success('Silindi.'); fetchData(); }
    catch { toast.error('Silinemedi.'); }
  };

  const handleToggle = async (y) => {
    try { await axiosInstance.patch(`/odeme-yontemi/update/${y._id}`,{aktif:!y.aktif}); fetchData(); }
    catch { toast.error('Güncelleme başarısız.'); }
  };

  const kasaGruplari = {};
  yöntemler
    .filter(y => !search || y._id.toLowerCase().includes(search.toLowerCase()) || y.displayName.toLowerCase().includes(search.toLowerCase()))
    .forEach(y => { if(!kasaGruplari[y.kasa]) kasaGruplari[y.kasa]=[]; kasaGruplari[y.kasa].push(y); });

  const aktif = yöntemler.filter(y=>y.aktif).length;

  return (
    <div style={P.page}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        .oy-card:hover { background:rgba(255,255,255,0.035) !important; }
        .oy-icon-btn:hover { background:rgba(255,255,255,0.08) !important; border-color:rgba(255,255,255,0.12) !important; }
        .oy-inp:focus { border-color:rgba(242,113,39,0.5) !important; }
        ::-webkit-scrollbar{width:5px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:#2d3238;border-radius:4px}
      `}</style>

      {/* HEADER */}
      <div style={P.header}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:4 }}>
            <div style={P.iconBadge}><FaCreditCard size={16}/></div>
            <h1 style={P.pageTitle}>Ödeme Yöntemleri</h1>
          </div>
          <p style={P.pageSub}>
            <span style={P.statPill}>{yöntemler.length} yöntem</span>
            <span style={{ ...P.statPill,color:'#22c55e',background:'rgba(34,197,94,0.08)',border:'1px solid rgba(34,197,94,0.15)' }}>{aktif} aktif</span>
            <span style={{ ...P.statPill,color:'#64748b',background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)' }}>{yöntemler.length-aktif} pasif</span>
          </p>
        </div>
        <div style={P.headerActions}>
          <div style={P.searchBox}>
            <FaSearch size={12} style={{ color:'#475569',flexShrink:0 }}/>
            <input className="oy-inp" style={P.searchInp} placeholder="Ara..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select style={P.filterSel} value={kasaFiltre} onChange={e=>setKasaFiltre(e.target.value)}>
            <option value="">Tüm kasalar</option>
            {KASALAR.map(k=><option key={k} value={k}>{k}</option>)}
          </select>
          <button className="oy-icon-btn" style={P.iconBtn} onClick={fetchData} title="Yenile">
            <FaSync size={13} style={loading?{animation:'spin 0.9s linear infinite'}:{}}/>
          </button>
          <button style={P.addBtn} onClick={openEkle}><FaPlus size={12}/> Yöntem Ekle</button>
        </div>
      </div>

      {/* GRID */}
      {loading ? (
        <div style={{ display:'flex',justifyContent:'center',padding:80 }}>
          <div style={{ width:32,height:32,borderRadius:'50%',border:'2.5px solid #1e2228',borderTopColor:'#f27127',animation:'spin 0.8s linear infinite' }}/>
        </div>
      ) : Object.keys(kasaGruplari).length===0 ? (
        <div style={{ textAlign:'center',padding:80,color:'#334155' }}>
          <FaCreditCard size={32} style={{ marginBottom:12,opacity:0.3 }}/>
          <p style={{ margin:0,fontSize:'0.9rem' }}>Kayıt bulunamadı</p>
        </div>
      ) : (
        <div style={P.grid}>
          {Object.entries(kasaGruplari).map(([kasa,liste],gi) => {
            const renk = KASA_RENK[kasa]??'#64748b';
            const aktifCount = liste.filter(y=>y.aktif).length;
            return (
              <div key={kasa} style={{ ...P.kasaCard,animationDelay:`${gi*0.04}s` }}>
                <div style={{ ...P.kasaHead,borderLeftColor:renk }}>
                  <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                    <div style={{ width:8,height:8,borderRadius:'50%',background:renk,boxShadow:`0 0 8px ${renk}80` }}/>
                    <span style={{ color:renk,fontWeight:800,fontSize:'0.82rem',letterSpacing:'0.08em',textTransform:'uppercase' }}>{kasa}</span>
                  </div>
                  <span style={{ color:'#334155',fontSize:'0.72rem',fontWeight:500 }}>{aktifCount}/{liste.length}</span>
                </div>
                <div style={{ padding:'6px 8px 10px',display:'flex',flexDirection:'column',gap:4 }}>
                  {liste.map(y => (
                    <div key={y._id} className="oy-card" style={{ ...P.yCard,opacity:y.aktif?1:0.45 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        <button style={{ background:'none',border:'none',cursor:'pointer',padding:0,color:y.aktif?renk:'#334155',display:'flex',flexShrink:0 }}
                          onClick={()=>handleToggle(y)} title={y.aktif?'Pasif yap':'Aktif yap'}>
                          {y.aktif?<FaToggleOn size={20}/>:<FaToggleOff size={20}/>}
                        </button>
                        <div style={{ flex:1,minWidth:0 }}>
                          <p style={{ color:'#e2e8f0',fontWeight:600,fontSize:'0.84rem',margin:'0 0 2px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{y.displayName}</p>
                          <p style={{ color:'#334155',fontSize:'0.68rem',margin:0,fontFamily:'monospace' }}>{y._id}</p>
                        </div>
                        <div style={{ display:'flex',gap:4,flexShrink:0 }}>
                          {[{l:'Y',v:y.komisyon?.yatirim,c:'#22c55e'},{l:'Ç',v:y.komisyon?.cekim,c:'#ef4444'},{l:'T',v:y.komisyon?.tahsilat,c:'#3b82f6'}].map(({l,v,c})=>(
                            <span key={l} style={{ fontSize:'0.65rem',fontWeight:700,padding:'2px 6px',borderRadius:5,background:`${c}12`,color:v>0?c:'#334155',border:`1px solid ${v>0?c+'30':'transparent'}` }}>
                              {l} %{v??0}
                            </span>
                          ))}
                        </div>
                        <div style={{ display:'flex',gap:3,flexShrink:0 }}>
                          <IconBtn title="Komisyon" color="#f59e0b" onClick={()=>openKomisyon(y)}><FaPercentage size={11}/></IconBtn>
                          <IconBtn title="Düzenle"  color="#3b82f6" onClick={()=>openDüzenle(y)}><FaEdit size={11}/></IconBtn>
                          <IconBtn title="Sil"      color="#ef4444" onClick={()=>handleSil(y)}><FaTrash size={11}/></IconBtn>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FORM MODAL - Ekle / Düzenle */}
      <Modal open={modal==='form'} onClose={closeModal} width={520}
        title={secili?'Yöntemi Düzenle':'Yeni Ödeme Yöntemi'}
        subtitle={secili?secili._id:'Yeni bir ödeme yöntemi ve komisyon oranları tanımlayın'}>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20 }}>
          <Field label="ID / PaymentSystemName" span={2}>
            <input ref={firstRef} className="oy-inp" style={{...textInputStyle(!!secili),width:'100%'}}
              value={form._id} readOnly={!!secili}
              onChange={e=>setForm(p=>({...p,_id:e.target.value}))} placeholder="FaynPay"/>
          </Field>
          <Field label="Görünen Ad">
            <input className="oy-inp" style={{...textInputStyle(false),width:'100%'}} value={form.displayName}
              onChange={e=>setForm(p=>({...p,displayName:e.target.value}))} placeholder="FaynPay Havale"/>
          </Field>
          <Field label="Kasa">
            <select style={{...textInputStyle(false),width:'100%',cursor:'pointer'}} value={form.kasa}
              onChange={e=>setForm(p=>({...p,kasa:e.target.value}))}>
              {KASALAR.map(k=><option key={k} value={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Durum" span={2}>
            <div style={{ display:'flex',gap:8 }}>
              {[{v:true,l:'Aktif'},{v:false,l:'Pasif'}].map(({v,l})=>(
                <button key={l} onClick={()=>setForm(p=>({...p,aktif:v}))}
                  style={{ flex:1,padding:'9px',borderRadius:9,border:`1px solid ${form.aktif===v?'rgba(242,113,39,0.5)':'rgba(255,255,255,0.06)'}`,background:form.aktif===v?'rgba(242,113,39,0.1)':'transparent',color:form.aktif===v?'#f27127':'#475569',fontWeight:600,fontSize:'0.85rem',cursor:'pointer',transition:'all 0.15s' }}>
                  {l}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.05)',paddingTop:16,marginBottom:20 }}>
          <p style={{ color:'#475569',fontSize:'0.72rem',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',margin:'0 0 12px',display:'flex',alignItems:'center',gap:6 }}>
            <FaPercentage size={10} style={{color:'#f27127'}}/> Komisyon Oranları
          </p>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10 }}>
            <KomInput label="Yatırım"  color="#22c55e" value={form.komisyon.yatirim}  onChange={v=>setForm(p=>({...p,komisyon:{...p.komisyon,yatirim:v}}))}/>
            <KomInput label="Çekim"    color="#ef4444" value={form.komisyon.cekim}    onChange={v=>setForm(p=>({...p,komisyon:{...p.komisyon,cekim:v}}))}/>
            <KomInput label="Tahsilat" color="#3b82f6" value={form.komisyon.tahsilat} onChange={v=>setForm(p=>({...p,komisyon:{...p.komisyon,tahsilat:v}}))}/>
          </div>
        </div>
        <div style={P.mFooter}>
          <button style={P.cancelBtn} onClick={closeModal}>Vazgeç</button>
          <button style={P.saveBtn} onClick={handleKaydet}>{secili?'Kaydet':'Ekle'}</button>
        </div>
      </Modal>

      {/* KOMİSYON MODAL - width=500, minWidth:0 ile tasma onlendi */}
      <Modal open={modal==='komisyon'} onClose={closeModal} width={500}
        title="Komisyon Oranları"
        subtitle={secili?`${secili.displayName} - ${secili.kasa}`:''}>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16 }}>
          <KomInput label="Yatırım"  color="#22c55e" value={form.komisyon.yatirim}  onChange={v=>setForm(p=>({...p,komisyon:{...p.komisyon,yatirim:v}}))}/>
          <KomInput label="Çekim"    color="#ef4444" value={form.komisyon.cekim}    onChange={v=>setForm(p=>({...p,komisyon:{...p.komisyon,cekim:v}}))}/>
          <KomInput label="Tahsilat" color="#3b82f6" value={form.komisyon.tahsilat} onChange={v=>setForm(p=>({...p,komisyon:{...p.komisyon,tahsilat:v}}))}/>
        </div>
        <div style={{ background:'rgba(255,255,255,0.015)',borderRadius:10,padding:'12px 14px',marginBottom:16 }}>
          <p style={{ color:'#334155',fontSize:'0.67rem',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.07em',margin:'0 0 10px' }}>
            Önizleme — 10.000 ₺ işlem
          </p>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8 }}>
            <PreviewCard label="Yatırım"  oran={form.komisyon.yatirim}  tutar={10000} renk="#22c55e"/>
            <PreviewCard label="Çekim"    oran={form.komisyon.cekim}    tutar={10000} renk="#ef4444"/>
            <PreviewCard label="Tahsilat" oran={form.komisyon.tahsilat} tutar={10000} renk="#3b82f6"/>
          </div>
        </div>
        <div style={P.mFooter}>
          <button style={P.cancelBtn} onClick={closeModal}>Vazgeç</button>
          <button style={P.saveBtn} onClick={handleKaydet}>Kaydet</button>
        </div>
      </Modal>

    </div>
  );
};

const P = {
  page:{ padding:'28px 28px 60px',background:'#0c0e10',minHeight:'100vh',fontFamily:"'DM Sans',sans-serif",color:'#f1f5f9',animation:'fadeUp 0.35s ease' },
  header:{ display:'flex',alignItems:'flex-start',gap:16,marginBottom:28,flexWrap:'wrap' },
  iconBadge:{ width:36,height:36,borderRadius:10,background:'rgba(242,113,39,0.12)',border:'1px solid rgba(242,113,39,0.2)',display:'flex',alignItems:'center',justifyContent:'center',color:'#f27127',flexShrink:0 },
  pageTitle:{ color:'#f1f5f9',fontSize:'1.25rem',fontWeight:800,margin:0,letterSpacing:'-0.02em' },
  pageSub:{ display:'flex',alignItems:'center',gap:6,margin:0,marginTop:6 },
  statPill:{ fontSize:'0.72rem',fontWeight:600,padding:'3px 10px',borderRadius:20,background:'rgba(242,113,39,0.08)',color:'#f27127',border:'1px solid rgba(242,113,39,0.15)' },
  headerActions:{ display:'flex',gap:8,alignItems:'center',flexWrap:'wrap' },
  searchBox:{ display:'flex',alignItems:'center',gap:8,background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:10,padding:'0 12px',height:36 },
  searchInp:{ background:'none',border:'none',outline:'none',color:'#e2e8f0',fontSize:'0.83rem',width:140,padding:'0' },
  filterSel:{ background:'#0c0e10',border:'1px solid rgba(255,255,255,0.06)',borderRadius:10,color:'#94a3b8',padding:'0 12px',height:36,fontSize:'0.82rem',cursor:'pointer',outline:'none' },
  iconBtn:{ width:36,height:36,borderRadius:10,border:'1px solid rgba(255,255,255,0.06)',background:'rgba(255,255,255,0.03)',color:'#64748b',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',transition:'all 0.15s' },
  addBtn:{ height:36,padding:'0 16px',borderRadius:10,background:'linear-gradient(135deg,#f27127,#ff9a44)',color:'#fff',border:'none',fontWeight:700,fontSize:'0.83rem',display:'flex',alignItems:'center',gap:6,cursor:'pointer',letterSpacing:'-0.01em' },
  grid:{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))',gap:16 },
  kasaCard:{ background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.05)',borderRadius:16,overflow:'hidden',animation:'fadeUp 0.3s ease both' },
  kasaHead:{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 14px',borderBottom:'1px solid rgba(255,255,255,0.04)',borderLeft:'3px solid',background:'rgba(255,255,255,0.01)' },
  yCard:{ background:'rgba(255,255,255,0.015)',borderRadius:10,padding:'10px 12px',transition:'background 0.15s',cursor:'default' },
  mFooter:{ display:'flex',gap:10,justifyContent:'flex-end',paddingTop:4 },
  cancelBtn:{ padding:'9px 18px',borderRadius:9,background:'rgba(255,255,255,0.04)',color:'#94a3b8',border:'1px solid rgba(255,255,255,0.06)',cursor:'pointer',fontWeight:600,fontSize:'0.85rem' },
  saveBtn:{ padding:'9px 22px',borderRadius:9,background:'linear-gradient(135deg,#f27127,#ff9a44)',color:'#fff',border:'none',cursor:'pointer',fontWeight:700,fontSize:'0.85rem' },
};

export default OdemeYöntemiYonetimi;
